<template>
  <div
    class="card accordion card-pool"
    :class="{ isTest: isTest, isNotActive: isNotActive }"
  >
    <AuAccordion
      :label="
        isOpen ? $t('auCapitals.pools.hide') : $t('auCapitals.pools.details')
      "
      :start-open="isOpen"
      @changeIsOpen="handleAccordionToggle"
    >
      <template #title>
        <div class="header">
          <div>
            <span>{{
              $t("auCapitals.pools.days", { n: currentProject.lockUpTerm })
            }}</span>
            <span>x{{ multiplier }} {{ $t("auCapitals.pools.multiplier") }}</span>
          </div>

          <div class="items">
            <div>
              <span>{{ $t("auCapitals.pools.deposited") }}</span>
              <AuDecimalStatic
                :value="currentProject.stake?.deposit"
                :decimal="currentProject.mnemonicDecimals"
                :indent="currentProject.mnemonicDecimals"
                :addition="currentProject.mnemonicEng"
              />
            </div>

            <div>
              <span>{{ $t("auCapitals.pools.earned") }}</span>
              <AuDecimalStatic
                :value="currentProject.stake?.earned"
                :decimal="currentProject.mnemonicDecimals"
                :indent="4"
                :addition="currentProject.mnemonicEng"
                zero-if-little
              />
            </div>

            <div>
              <span>{{ $t("auCapitals.pools.APR") }}</span>
              <span>{{ currentProject.profitability }}%</span>
            </div>

            <div>
              <span>{{ $t("auCapitals.pools.remaning") }}</span>
              <AuDecimalStatic
                :value="remaning"
                :decimal="currentProject.mnemonicDecimals"
                :indent="currentProject.mnemonicDecimals"
                :addition="currentProject.mnemonicEng"
                none-if-empty
              />
            </div>

            <div>
              <span>{{ $t("auCapitals.pools.lockUpTerm") }}</span>
              <span>{{
                $t("auCapitals.pools.days", { n: currentProject.lockUpTerm })
              }}</span>
            </div>

            <div>
              <span>{{ $t("auCapitals.pools.withdrawalDelayTime") }}</span>
              <AuDecimalStatic
                :value="currentProject.withdrawalDelayTime"
                :decimal="0"
                :indent="0"
                :show-addition="false"
                none-if-empty
              />
            </div>
          </div>
        </div>
      </template>
      <template #content>
        <div class="content">
          <div>
            <ProgressBarWrapper
              :procent="currentProject.progressProcent"
              green
            >
              <template #top-left>
                <span>{{ $t("auCapitals.pools.totalAmount") }}</span>
              </template>

              <template #top-right>
                <AuDecimalStatic
                  :value="currentProject.maxCap"
                  :decimal="currentProject.mnemonicDecimals"
                  :addition="currentProject.mnemonicEng"
                />
              </template>

              <template #bottom-left>
                <span>{{ currentProject.progressProcent + "%" }}</span>
              </template>
            </ProgressBarWrapper>
            <AuList :items="items" />
          </div>
          <div>
            <AuButton
              type="primary"
              :disabled="isNotActive"
              @click="showDialog = true"
            >
              {{ $t("auCapitals.pools.invest") }}
            </AuButton>
          </div>
        </div>
      </template>
    </AuAccordion>
    <CapitalModal
      v-if="showDialog"
      v-model="showDialog"
      :project="currentProject"
      @finish="investFinished"
    />
  </div>
</template>

<script>
import BigNumber from "bignumber.js";
import CapitalModal from "@/components/capital/CapitalModal";
import ProgressBarWrapper from "@/components/progressBar/ProgressBarWrapper.vue";
import { isUserLevel } from "@/help/systemSettings";
import { mapActions, mapGetters } from "vuex";

export default {
  components: {
    ProgressBarWrapper,
    CapitalModal
  },

  props: {
    project: {
      type: Object,
      default: null
    },
    activeProjectId: {
      type: [String, Number],
      default: null
    }
  },

  data() {
    return {
      isOpen: false,
      showDialog: false,
      currentProject: {}
    };
  },

  computed: {
    ...mapGetters({
      isTestRole: "profile/isTestRole",
      auCapital: "capitals/auCapital",
      profile: "profile/profile",
      apiSystemSettings: "apiSystemSettings"
    }),

    isTest() {
      return !this.project?.visibility;
    },

    isNotActive() {
      return (
        this.project.status !== "Open" ||
        this.remaning == 0 ||
        this.project.progressProcent >= 100 ||
        !isUserLevel()
      );
    },

    items() {
      const titleStyle = { color: "var(--text-color)" };

      const addition = this.currentProject.mnemonicEng;

      return [
        [
          {
            title: this.$t("auCapitals.pools.startTime"),
            value: this.currentProject.dateStart,
            titleStyle
          },
          {
            title: this.$t("auCapitals.pools.endTime"),
            value: this.currentProject.dateEnd,
            titleStyle
          },
          {
            title: this.$t("auCapitals.pools.stakeAmount"),
            value: this.currentProject.minCount,
            titleStyle,
            decimal: 0,
            addition,
            noneIfEmpty: true
          },
          {
            title: this.$t("auCapitals.pools.maximumPerWallet"),
            value: this.currentProject.quote,
            titleStyle,
            decimal: 0,
            addition,
            noneIfEmpty: true
          }
        ]
      ];
    },

    remaning() {
      return BigNumber(this.currentProject.maxCap)
        .minus(this.currentProject.currentCap)
        .toNumber()
        .toString();
    },

    multiplier() {
      return Number((this.currentProject.profitability / 10).toFixed(1));
    }
  },

  watch: {
    auCapital: {
      async handler(newVal) {
        if (this.currentProject.id && this.currentProject.id == newVal?.id) {
          const item = newVal;
          this.currentProject.currentCap = item.currentCap;

          const earned = await this.getStakingPoolsEarned();

          const currentEarned = earned.find(
            (item) => item.event === this.currentProject.id
          );
          if (currentEarned && this.currentProject?.stake) {
            this.currentProject.stake.deposit = currentEarned.deposit;
          }

          let percent = BigNumber(this.currentProject.currentCap);
          percent = percent.multipliedBy(100);
          percent = percent.dividedBy(this.currentProject.maxCap);
          this.currentProject.progressProcent = percent.toNumber().toFixed(1);
        }
      },
      deep: true,
      immediate: true
    }
  },

  created() {
    this.currentProject = this.project;
    if (String(this.currentProject.id) == String(this.activeProjectId)) {
      this.isOpen = true;
    }
  },

  methods: {
    ...mapActions({
      getAuCapitals: "capitals/getAuCapitals",
      getStakingPoolsEarned: "capitals/getStakingPoolsEarned"
    }),

    handleAccordionToggle(isOpen) {
      this.isOpen = isOpen;
      if (isOpen) {
        this.$router.replace({
          query: {
            ...this.$route.query,
            projectId: this.currentProject.id
          }
        });
      }
      else {
        const query = { ...this.$route.query };
        delete query.projectId;
        this.$router.replace({ query });
      }
    },

    async investFinished() {
      if (process.env.VUE_APP_WEBSOCKET_ENABLED != "true") {
        await new Promise((r) => setTimeout(r, 20000));
        await this.updateProject();
      }
    },

    async updateProject() {
      const earned = await this.getStakingPoolsEarned();

      const currentEarned = earned[this.currentProject.id];
      this.currentProject.stake.deposit = currentEarned.deposit;

      const item = await this.getAuCapitals(this.currentProject.id);
      this.currentProject.currentCap = item.currentCap;
      let percent = BigNumber(this.currentProject.currentCap);
      percent = percent.multipliedBy(100);
      percent = percent.dividedBy(this.currentProject.maxCap);
      this.currentProject.progressProcent = percent.toNumber().toFixed(1);
    }
  }
};
</script>

<style lang="scss" scoped>
.card-pool {
  background: var(--card-pool-color);
}

.accordion {
  padding: 20px;
}

.isTest {
  background: var(--tester-color);
}

.isNotActive {
  background-color: var(--no-active-color);
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  @media screen and (max-width: 1300px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  > div:first-child {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin-bottom: 1px;

    width: 15%;

    span:first-child {
      font-size: 24px;
      line-height: 20px;
      text-wrap: nowrap;
    }

    span:last-child {
      color: var(--color-50);
      font-size: 12px;
      text-wrap: nowrap;
    }
  }

  .items {
    display: grid;
    grid-template-columns: 1fr 1fr 0.6fr 1.4fr 1fr 1.2fr;
    align-items: center;

    width: 85%;

    @media screen and (max-width: 1300px) {
      width: 108%;

      border-top: 1px dashed var(--color-70);
      padding-top: 10px;

      > div {
        padding-right: 15px !important;
        padding-left: 15px !important;
      }

      > div:first-child {
        border-left: none !important;
        padding-left: 0px !important;
      }

      > div:last-child {
        border-right: none !important;
        margin-right: 0px !important;
        padding-right: 0px !important;
      }
    }

    @media screen and (max-width: 900px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 10px;

      > div {
        padding: 0px !important;
        border: none !important;
        width: auto !important;
      }
    }

    @media screen and (max-width: 400px) {
      grid-template-columns: repeat(1, minmax(0, 1fr));
    }

    > div {
      display: flex;
      flex-direction: column;
      justify-content: center;

      padding-left: 20px;
      padding-right: 20px;
      border-left: 1px dashed var(--color-70);

      height: 100%;

      span:first-child {
        color: var(--color-50);
        line-height: 12px;
        font-size: 12px;
      }

      span:last-child {
        font-size: 16px;
      }
    }

    > div:last-child {
      border-right: 1px dashed var(--color-70);
      margin-right: 40px;
    }
  }
}

.content {
  display: flex;
  gap: 20px;

  @media screen and (max-width: 900px) {
    flex-direction: column;
  }

  > div:first-child {
    display: flex;
    flex-direction: column;
    gap: 10px;

    width: 50%;

    @media screen and (max-width: 1300px) {
      width: 60%;
    }

    @media screen and (max-width: 900px) {
      width: 100%;
    }

    > div {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }
  }
}
</style>

<style lang="scss">
.card.accordion {
  width: 100%;

  .au-accordion-header {
    border-bottom-color: var(--color-70);
    border-bottom-style: solid;
    border-top: none;

    padding-top: 0px;
    margin-bottom: -9px;

    > div:first-child {
      width: 100%;
    }

    .chevron__wrap {
      color: #279063;
      width: 80px;
      justify-content: end;

      @media screen and (max-width: 1300px) {
        align-self: start;
        margin-top: 5px;
      }
    }
  }

  .au-accordion-body {
    padding: 22px 0 0px 0;
  }

  .au-accordion-toggler {
    filter: brightness(0) saturate(100%) invert(27%) sepia(56%) saturate(748%)
      hue-rotate(122deg) brightness(92%) contrast(89%);
  }
}
</style>
